/**
 * LevelItemsList component for rendering level items as accordions.
 * Extracted from FarmDetailsModal.tsx.
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { format } from "date-fns";
import { FormAnswersViewer } from "../../forms/FormAnswersViewer";
import TextField from "@mui/material/TextField";
import { isEndDatePreviousYear } from "./farmDetailsUtils";

interface LevelItemsListProps {
  levelName: string;
  pvis: any[];
  farmItemsMap: Record<string, any>;
  showTitle?: boolean;
  formsMap?: Record<string, any>;
  answersMap?: Record<string, any[]>;
  allExpanded?: boolean;
  expandToggleKey?: number;
}

export const LevelItemsList: React.FC<LevelItemsListProps> = React.memo(({
  levelName,
  pvis,
  farmItemsMap,
  showTitle = true,
  formsMap = {},
  answersMap = {},
  allExpanded,
  expandToggleKey,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (allExpanded) {
      setExpandedIds(new Set(pvis.map((pvi) => pvi.id)));
    } else {
      setExpandedIds(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandToggleKey]);

  const handleChange = (id: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (isExpanded) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };
  return (
    <Box>
      {showTitle && (
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1.5 }}>
          {levelName}
        </Typography>
      )}
      <Box display="flex" flexDirection="column" gap={2}>
        {pvis.map((pvi) => {
          const farmItem = farmItemsMap[pvi.id];

          // Use CSS variable-based colors
          let bgColor = "var(--text-disabled)"; // Default grey for pending/unknown
          let borderColor = "var(--text-disabled)";
          let textColor = "#fff";

          if (farmItem) {
            if (farmItem.isApproved) {
              bgColor = "var(--success-light)";
              borderColor = "var(--success-light)";
            } else if (farmItem.isActive) {
              bgColor = "var(--warning-light)";
              borderColor = "var(--warning-light)";
            }
          }

          // Special case approved but empty/old value
          const isApprovedButEmptyValue =
            farmItem &&
            farmItem.isApproved &&
            isEndDatePreviousYear(farmItem.endDate);
          if (isApprovedButEmptyValue) {
            bgColor = "var(--info-light)";
            borderColor = "var(--info-light)";
            textColor = "var(--info-dark)";
          }

          return (
            <Accordion
              key={pvi.id}
              expanded={expandedIds.has(pvi.id)}
              onChange={handleChange(pvi.id)}
              sx={{
                border: "1px solid var(--border-color)",
                borderRadius: "12px !important",
                "&:before": { display: "none" },
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
                transition: "all 150ms ease",
                "&:hover": {
                  borderColor: "var(--primary)",
                  boxShadow: "0 2px 8px rgba(11, 15, 40, 0.08)",
                },
                "&.Mui-expanded": {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: "var(--text-secondary)" }} />}
                sx={{
                  "&.Mui-expanded": {
                    minHeight: 48,
                  },
                  "& .MuiAccordionSummary-content.Mui-expanded": {
                    margin: "12px 0",
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      backgroundColor: bgColor,
                      border: `2px solid ${borderColor}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                      fontSize: "0.85rem",
                      color: textColor,
                      flexShrink: 0,
                    }}
                  >
                    {pvi.cpNumber?.split(".").pop() || "?"}
                  </Box>
                  <Box flexGrow={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "var(--primary)" }}>
                      {pvi.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "var(--text-secondary)" }}>
                      {pvi.cpNumber}
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ borderTop: "1px solid var(--border-color)" }}>
                {/* Description */}
                {pvi.description && (
                  <Typography
                    variant="body2"
                    sx={{ mb: 2, color: "var(--text-secondary)" }}
                  >
                    {pvi.description}
                  </Typography>
                )}

                {farmItem && (
                  <>
                    {/* Comments */}
                    {farmItem.comments && (
                      <Box mb={2}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "var(--primary)" }}>
                          Comentarios:
                        </Typography>
                        <Typography variant="body2" sx={{ color: "var(--text-primary)" }}>
                          {farmItem.comments}
                        </Typography>
                      </Box>
                    )}

                    {/* Dates */}
                    {(farmItem.startDate || farmItem.endDate) && (
                      <Box mb={2} display="flex" gap={3}>
                        {farmItem.startDate && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "var(--text-secondary)", fontWeight: 500 }}
                              display="block"
                            >
                              Inicio
                            </Typography>
                            <Typography variant="body2" sx={{ color: "var(--text-primary)" }}>
                              {format(
                                new Date(farmItem.startDate),
                                "dd/MM/yyyy",
                              )}
                            </Typography>
                          </Box>
                        )}
                        {farmItem.endDate && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "var(--text-secondary)", fontWeight: 500 }}
                              display="block"
                            >
                              Fin
                            </Typography>
                            <Typography variant="body2" sx={{ color: "var(--text-primary)" }}>
                              {format(new Date(farmItem.endDate), "dd/MM/yyyy")}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Value / Answers */}
                    {(farmItem.value || (pvi.formId && answersMap[farmItem.id]?.length > 0)) && (
                      <Box mt={1}>
                        {pvi.formId && formsMap[pvi.formId] ? (
                          // Use new forms API
                          <Box
                            sx={{
                              p: 2.5,
                              backgroundColor: "var(--tertiary)",
                              borderRadius: 2,
                            }}
                          >
                            <FormAnswersViewer
                              form={formsMap[pvi.formId]}
                              answers={answersMap[farmItem.id] || []}
                            />
                          </Box>
                        ) : pvi.measurementTemplate ? (
                          // Fallback to legacy
                          <Box
                            sx={{
                              p: 2.5,
                              backgroundColor: "var(--tertiary)",
                              borderRadius: 2,
                            }}
                          >
                            <FormAnswersViewer
                              formTemplateJson={pvi.measurementTemplate}
                              formAnswersJson={farmItem.value}
                            />
                          </Box>
                        ) : (
                          <TextField
                            label="Valor"
                            value={farmItem.value}
                            fullWidth
                            disabled
                            variant="outlined"
                            size="small"
                          />
                        )}
                      </Box>
                    )}
                  </>
                )}

                {!farmItem && (
                  <Typography
                    variant="body2"
                    sx={{ color: "var(--text-secondary)", fontStyle: "italic" }}
                  >
                    No hay datos registrados para este ítem.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );
});
LevelItemsList.displayName = "LevelItemsList";
